import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { useDeviceSize, ContentType } from '@atlassian/learning-components';

import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { LEARNING_PATH_DISCLAIMER_TEXT, COURSE_DISCLAIMER_TEXT } from 'src/pages/LearningPathPage/constants';
import { ContentfulSmCourseDisclaimer } from 'src/graphql/queries/GetSmCourse';
import { ContentfulLearningPathDisclaimer } from 'src/graphql/queries/GetLearningPath';

interface DisclaimerProps {
  disclaimer?: ContentfulSmCourseDisclaimer | ContentfulLearningPathDisclaimer;
  entryId?: string;
  contentType: ContentType.COURSE | ContentType.LEARNING_PATH;
}

export const Disclaimer: React.FC<DisclaimerProps> = ({ disclaimer, entryId, contentType }) => {
  const inspectorProps = useContentfulInspectorMode();
  const deviceWidth = useDeviceSize();

  return (
    <div css={disclaimerContentSectionStyles}>
      <div id="disclaimer" css={disclaimerContainerStyles} data-testid="disclaimer">
        <div css={disclaimerInfoCardSpacerStyles} />
        <div css={disclaimerSectionStyles}>
          {disclaimer ? (
            <div {...(entryId && inspectorProps({ entryId, fieldId: 'disclaimer' }))}>
              <div css={disclaimerContentStyles}>
                {documentToReactComponents(disclaimer.json, getOptions(disclaimer, deviceWidth))}
              </div>
            </div>
          ) : (
            <div css={disclaimerTextStyles}>
              {contentType === ContentType.COURSE ? COURSE_DISCLAIMER_TEXT : LEARNING_PATH_DISCLAIMER_TEXT}
              <a
                href="https://www.atlassian.com/licensing/purchase-licensing#pricing-discounts"
                css={disclaimerLinkStyles}
              >
                visit our FAQs.
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const disclaimerContentSectionStyles = css({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '1200px',
  margin: '0 auto',
  justifyContent: 'flex-start',
  paddingTop: '21px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  paddingRight: token('space.300'),

  '@media (max-width: 965px)': {
    order: '4',
    flexDirection: 'column',
    paddingLeft: token('space.200'),
    paddingRight: token('space.200'),
  },

  '@media (max-width: 1200px)': {
    order: '4',
    maxWidth: '100%',
  },
});

const disclaimerContainerStyles = css({
  maxWidth: '753px',
  width: '100%',
  margin: '0 auto 0 0',
});

const disclaimerInfoCardSpacerStyles = css({
  width: '395px',
  height: 0,
  display: 'block',
  '@media (max-width: 965px)': {
    display: 'none',
  },
});

const disclaimerSectionStyles = css({
  width: '100%',
});

const disclaimerTextStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
});

const disclaimerLinkStyles = css({
  color: token('color.text.accent.blue'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  textDecorationLine: 'underline',
});

const disclaimerContentStyles = css({
  '& span': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },
  '& a': {
    color: `${token('color.text.accent.blue')} !important`,
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    textDecorationLine: 'underline',
  },
  '& div': {
    margin: 0,

    '@media (max-width: 965px)': {
      maxWidth: 'unset !important',
    },
  },
});
